import { Directive, ElementRef, HostListener, inject, AfterViewInit, OnDestroy, input, effect } from '@angular/core';
import { guid } from '@fi-app/shared/utils/guid';

@Directive({
    selector: 'input[fiInputAutoWidth], textarea[fiInputAutoWidth]',
    standalone: false,
})
export class FiInputAutoWidthDirective implements AfterViewInit, OnDestroy {
    private mirror: HTMLDivElement = document.createElement('div');
    private elementRef: ElementRef = inject(ElementRef);
    private input: HTMLInputElement | HTMLTextAreaElement = this.elementRef.nativeElement;

    public fiInputAutoWidthEnabled = input<boolean>(true);

    private uid = guid();

    constructor() {
        effect(() => {
            if (this.fiInputAutoWidthEnabled()) {
                this.createMirrorElement();
                this.updateSize();
            }
        });
    }

    @HostListener('input') public onInput(): void {
        this.updateSize();
    }

    public ngAfterViewInit(): void {
        setTimeout(() => {
            this.createMirrorElement();
            this.updateSize();
        }, 0);
    }

    private createMirrorElement(): void {
        if (!this.fiInputAutoWidthEnabled()) {
            return;
        }
        this.mirror.style.position = 'absolute';
        this.mirror.style.visibility = 'hidden';
        this.mirror.style.whiteSpace = 'pre';
        this.mirror.style.pointerEvents = 'none';
        this.mirror.id = this.uid;

        const computedStyle = window.getComputedStyle(this.input);
        const stylesToCopy = [
            'font-size',
            'font-family',
            'font-weight',
            'letter-spacing',
            'padding',
            'border',
            'margin',
            'box-sizing',
        ];

        stylesToCopy.forEach((style) => {
            this.mirror.style[style as any] = computedStyle.getPropertyValue(style);
        });

        document.body.appendChild(this.mirror);
    }

    private updateSize(): void {
        if (!this.fiInputAutoWidthEnabled()) {
            return;
        }

        const value = this.input.value;
        const placeholder = this.input.placeholder || '';

        if (placeholder && !value?.length) {
            this.mirror.textContent = placeholder;
        } else if (value) {
            this.mirror.textContent = value;
        }

        const finalWidth = this.mirror.offsetWidth + 2;
        this.input.style.width = finalWidth + 'px';
    }

    public ngOnDestroy(): void {
        if (this.mirror && this.mirror.parentNode) {
            this.mirror.parentNode.removeChild(this.mirror);
        }
    }
}
